<template>
  <b-row :class="[stateClass, sizeClass]">
    <b-col cols="6">
      <div
        v-if="isEmptySelection"
        class="py-5 d-inline-flex align-items-center justify-content-center flex-column w-100"
      >
        <font-awesome-icon icon="inbox" size="lg" class="text-gray mb-2" />
        <h4 class="mb-0">Nada por aqui</h4>
        <p class="text-muted" v-text="placeholderEmptyResults" />
      </div>
      <div v-else class="pb-1 pr-3 bg-white header-search">
        <b-form-input
          v-model="search_selected"
          placeholder="Buscar municípios selecionados..."
        />
      </div>
      <recycle-scroller
        :item-size="58"
        :buffer="2600"
        :items="formattedItems"
        class="scroller"
        list-class="list-group"
        item-class="list-group-item custom-list-group-item"
      >
        <template #default="{ item, index }">
          <div class="w-100 d-inline-flex align-items-center">
            <font-awesome-icon
              icon="check"
              size="xs"
              class="mr-2 text-exitos"
            />
            <span v-text="item.label" class="text-exitos" />
            <button
              class="ml-auto border-0 bg-transparent text-danger"
              @click="removeMunicipio(index)"
            >
              <font-awesome-icon icon="trash" />
            </button>
          </div>
        </template>
      </recycle-scroller>
      <div
        v-if="displayCountMunicipios"
        class="d-inline-flex align-items-center pr-3 w-100"
      >
        <div
          v-text="selectedLabel"
          class="py-3 px-2 text-muted font-italic text-sm"
        />
        <confirm-click class="ml-auto align-self-center" @call="removeAll">
          <template #default="{ confirmed }">
            <b-button size="sm" class="border-0" variant="outline-danger">
              <font-awesome-icon
                v-if="confirmed"
                icon="exclamation"
                class="mr-1"
              />
              Remover todos
            </b-button>
          </template>
        </confirm-click>
      </div>
    </b-col>
    <b-col cols="2">
      <VueTypeaheadBootstrap
        v-model="search_state"
        @hit="selectEstado"
        :data="estados"
        :serializer="(item) => item.nome + ' - ' + item.sigla"
        placeholder="Buscar estados..."
        :class="[stateClass]"
        :showOnFocus="true"
        :size="size"
        :disabled="disabled"
        :max-matches="maxMatchesStates"
      />
    </b-col>
    <b-col>
      <VueTypeaheadBootstrap
        v-model="search"
        :data="municipios"
        :serializer="(item) => item.estado_sigla + ' / ' + item.nome"
        placeholder="Buscar municípios..."
        @input="findMunicipio"
        @hit="addMunicipio"
        class="form-control form-control-custom"
        :size="size"
        :disabled="disabled || waiting_state"
        :max-matches="maxMatchesCities"
      />
    </b-col>
  </b-row>
</template>

<script>
import VueTypeaheadBootstrap from "vue-typeahead-bootstrap";
import { mapActions, mapState } from "vuex";
import { debounce, find } from "lodash";
import Fuse from "fuse.js";

import {
  formControlMixin,
  props as formControlProps,
} from "bootstrap-vue/src/mixins/form-control";

import {
  formStateMixin,
  props as formStateProps,
} from "bootstrap-vue/src/mixins/form-state";

import { sortKeys } from "bootstrap-vue/src/utils/object";
import { makePropsConfigurable } from "bootstrap-vue/src/utils/props";
import { safeArray, size } from "@/helpers/functions/ArrayHelpers";
import { stripAccents } from "@/helpers/functions/StringHelpers";
import ConfirmClick from "@/components/ConfirmClick.vue";

const props = makePropsConfigurable(
  sortKeys({
    ...formStateProps,
    ...formControlProps,
    value: {
      type: Array,
      default: [],
    },
    placeholder: {
      type: String,
      default: "Nenhum município selecionado",
    },
    uf: {
      type: String,
      default: null,
    },
    return_object: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "md",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  })
);

export default {
  components: { VueTypeaheadBootstrap, ConfirmClick },
  mixins: [formControlMixin, formStateMixin],
  props,
  data: () => ({
    search: "",
    search_selected: "",
    municipios: [],
    estados: [],
    search_state: "",
    estado_sigla: null,
    waiting_state: true,
  }),
  computed: {
    ...mapState("municipios", ["pagination"]),
    maxMatchesStates() {
      const MAX_KNOW_MATCHS = 27;

      if (size(this.estados) < MAX_KNOW_MATCHS) return 10;
      return MAX_KNOW_MATCHS;
    },
    existsSearch() {
      return !!stripAccents(this.search_selected);
    },
    formattedItems() {
      const list = safeArray(this.value).map((item) => ({
        label: [item.nome, item.estado_sigla].join(" / "),
        value: item.id,
        id: item.id,
        sigla: item.estado_sigla,
        size: 48,
        raw: item,
      }));

      if (this.existsSearch) {
        const result = new Fuse(list, {
          keys: ["label", "sigla"],
          threshold: 0.25,
        });

        return result.search(this.search_selected);
      }

      return list;
    },
    selectedLabel() {
      const count = size(this.formattedItems);
      const suffix = this.existsSearch ? "encontrados" : "selecionados";

      if (count === 1) {
        const suffix = this.existsSearch ? "encontrado" : "selecionado";
        return `${count} Município ${suffix}`;
      }

      return `${count} Municípios ${suffix}`;
    },
    isEmptySelection() {
      return size(this.formattedItems) === 0;
    },
    maxMatchesCities() {
      const MAX_CITIES_MATCHS = 500;
      const total = Number(this.pagination?.total) || 0;
      const perPage = Number(this.pagination?.per_page) || 0;

      if (total > perPage) return total;

      return MAX_CITIES_MATCHS;
    },
    sizeClass() {
      return "form-control-" + this.size;
    },
    displayCountMunicipios() {
      return !this.isEmptySelection || this.existsSearch;
    },
    placeholderEmptyResults() {
      return !!this.existsSearch
        ? `Nenhum resultado encontrado`
        : this.placeholder;
    },
  },
  async created() {
    this.estados = await this.ActionGetAllEstados();
  },
  methods: {
    ...mapActions("municipios", [
      "ActionFind",
      "ActionFindById",
      "ActionGetAllEstados",
    ]),
    findMunicipio: debounce(async function () {
      const payload = {
        q: this.search,
        estado_sigla: this.estado_sigla,
        per_page: 500,
      };

      if (this.uf) payload.estado_sigla = this.uf;

      const municipios = await this.ActionFind(payload);
      this.municipios = municipios.data;
    }, 500),
    addMunicipio(municipio) {
      if (municipio) {
        this.search = "";
        this.search_state = "";
        this.municipios = [];
        const has_mun = find(this.value, ["id", municipio.id]);

        if (!has_mun) {
          this.value.push(municipio);
          this.waiting_state = true;
          this.estado_sigla = null;
          this.$emit("input", this.value);
        } else {
          this.$notifications.addNotification({
            message: "Este município ja foi selecionado",
            icon: "exclamation-triangle",
            type: "warning",
          });
        }
      }
    },
    selectEstado(state) {
      this.waiting_state = false;
      this.estado_sigla = state.sigla;
    },
    removeMunicipio(index) {
      this.search_selected = "";
      this.value.splice(index, 1);
      this.$emit("input", this.value);
    },
    removeAll() {
      this.search_selected = "";
      this.$emit("input", []);
    },
  },
};
</script>

<style lang="scss">
.form-control-custom {
  margin-left: 0 !important;
  padding-left: 0 !important;
  padding-top: 0 !important;

  .form-control {
    border: none !important;
    background: transparent !important;
  }
  &:has([disabled="disabled"]) {
    background-color: #e9ecef !important;
  }
}

.scroller {
  max-height: 300px;
  overflow-y: auto;

  .vue-recycle-scroller__slot {
    position: sticky;
    top: 0;
    z-index: 999;
  }

  .custom-list-group-item.hover {
    background-color: rgba(var(--exitos-rgb), 0.05);
  }
}

.list-municipios-item-scroll {
  height: 100%;
}
</style>
